import { faChevronRight } from '@fortawesome/pro-regular-svg-icons';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button, HookFormPhoneInput } from '@realtyka/real-ui';
import { isValidPhoneNumber } from 'libphonenumber-js';
import React from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { z } from 'zod';
import NotificationMessage from '../../../components/NotificationMessage';
import useDeviceType from '../../../hooks/useDeviceType';
import { MfaControllerApi } from '../../../openapi/keymaker';
import ErrorService from '../../../services/ErrorService';
import {
  showErrorToast,
  showSuccessToast,
} from '../../../slices/ToastNotificationSlice';
import { useAppDispatch } from '../../../slices/store';
import { handleSessionExpiration } from '../../../utils/MfaErrorHandlingUtils';
import { getKeymakerConfiguration } from '../../../utils/OpenapiConfigurationUtils';

export const mfaPhoneNumberSchema = z.object({
  phoneNumber: z
    .string({
      required_error: 'Please enter your phone number',
    })
    .trim()
    .min(1, { message: 'Please enter your phone number' })
    .refine(
      (phone) => {
        if (!phone) return false;
        return isValidPhoneNumber(phone, 'US');
      },
      {
        message: 'Please enter a valid phone number',
      },
    ),
});

type FormData = z.infer<typeof mfaPhoneNumberSchema>;

interface MfaPhoneNumberCardProps {
  onSendMfaSms(value: string): void;
  redirectTo?: string;
  isAgentContext?: boolean;
}

const MfaPhoneNumberCard: React.FC<MfaPhoneNumberCardProps> = ({
  onSendMfaSms,
  redirectTo,
  isAgentContext = false,
}) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { isMobile } = useDeviceType();
  const {
    control,
    handleSubmit,
    formState: { isValid },
  } = useForm<FormData>({
    resolver: zodResolver(mfaPhoneNumberSchema),
    mode: 'onChange',
  });

  const onSubmit = async (values: FormData) => {
    try {
      await new MfaControllerApi(await getKeymakerConfiguration()).sendMfaSms(
        values.phoneNumber,
      );
      onSendMfaSms(values.phoneNumber);
      dispatch(
        showSuccessToast(
          'Successfully sent authentication code to phone number.',
        ),
      );
    } catch (e: any) {
      const handled = await handleSessionExpiration({
        error: e,
        dispatch,
        navigate,
        isAgentContext,
        redirectTo,
      });

      if (handled) {
        return;
      }
      ErrorService.notify(
        'Unable to send authentication code to phone number',
        e,
        {
          data: { phoneNumber: values.phoneNumber },
        },
      );
      dispatch(
        showErrorToast(
          'We had a problem sending authentication code to phone number',
          'Please try again in a few moments.',
        ),
      );
    }
  };

  return (
    <form
      className='flex flex-col gap-6 w-full mt-6'
      onSubmit={handleSubmit(onSubmit)}
    >
      <div className='flex flex-col gap-2 items-start self-stretch'>
        <div className='text-ellipsis font-inter-bold text-[15px] leading-[22px]'>
          Enter your mobile phone number
        </div>
        <HookFormPhoneInput
          control={control}
          name='phoneNumber'
          label='Mobile Number'
        />
      </div>
      <NotificationMessage message="We'll send you a text message with a confirmation code to this number" />
      <Button
        type='submit'
        fullWidth
        color='primary'
        size={isMobile ? 'sm' : 'md'}
        disabled={!isValid}
        rightIcon={faChevronRight}
      >
        <div className='font-medium'>Send Confirmation Code</div>
      </Button>
    </form>
  );
};

export default MfaPhoneNumberCard;
